import { ApplicationLayout } from '@folio/components';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { useFeatureFlag } from 'korint-sdk/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  type GetBrokerageFirmOutputFirmTypeEnumKey,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import {
  ArchiveIcon,
  Banknote,
  BarChart2,
  BotMessageSquare,
  Building2,
  FileTextIcon,
  HomeIcon,
  ListChecksIcon,
  PackageIcon,
  Table2,
  Users,
} from 'lucide-react';
import { GarantiePlusLogo } from '../components/logo/GarantiePlusLogo';

export function BrokerDashboardLayout() {
  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user.brokerageFirmId!;

  const {
    data: { firmType },
  } = useSuspenseQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
  });

  const { can } = useUserPermissions({ actions: ['CREATE_CONVERSATION', 'GET_REPORT'] });
  const commissionStatementsEnabled = useFeatureFlag('feat_commission_statements');

  const items: {
    label: string;
    icon: React.ReactNode;
    link: string;
    forTypes?: GetBrokerageFirmOutputFirmTypeEnumKey[];
  }[] = [
    {
      label: 'Accueil',
      link: '/ongoing-new-business',
      icon: <HomeIcon className="size-4" />,
      forTypes: ['WHOLESALE', 'RETAIL'],
    },
    {
      label: 'Portefeuille',
      link: '/portfolio',
      icon: <ListChecksIcon className="size-4" />,
      forTypes: ['WHOLESALE', 'RETAIL'],
    },
    {
      label: 'Devis archivés',
      link: '/archived-new-business',
      icon: <ArchiveIcon className="size-4" />,
      forTypes: ['WHOLESALE', 'RETAIL'],
    },
    {
      label: 'Utilisateurs',
      link: '/intermediary-users-list',
      icon: <Users className="size-4" />,
      forTypes: ['WHOLESALE', 'RETAIL'],
    },
    {
      label: 'Mes documents',
      link: `/my-documents/${brokerageFirmId}`,
      icon: <FileTextIcon className="size-4" />,
      forTypes: ['RETAIL'],
    },
    {
      label: 'Distributeurs',
      link: '/brokerage-firm-list',
      icon: <Building2 className="size-4" />,
      forTypes: ['WHOLESALE'],
    },
    {
      label: 'Rapports',
      icon: <Table2 className="size-4" />,
      link: '/reports',
      forTypes: ['WHOLESALE'],
    },
    ...(commissionStatementsEnabled && can.GET_REPORT
      ? [
          {
            label: 'Commissions',
            icon: <Banknote className="size-4" />,
            link: '/commissions',
            forTypes: ['WHOLESALE', 'RETAIL'] satisfies GetBrokerageFirmOutputFirmTypeEnumKey[],
          },
        ]
      : []),
    {
      label: 'Produits',
      icon: <PackageIcon className="size-4" />,
      link: '/products',
      forTypes: ['WHOLESALE', 'RETAIL'],
    },
    {
      label: 'Statistiques',
      icon: <BarChart2 className="size-4" />,
      link: '/analytics',
      forTypes: ['WHOLESALE'],
    },
    ...(can.CREATE_CONVERSATION
      ? [
          {
            label: 'Garantie Plus Assistant',
            link: '/conversations',
            icon: <BotMessageSquare className="size-4" />,
          },
        ]
      : []),
  ];

  return (
    <ApplicationLayout
      items={items.filter(({ forTypes }) => !forTypes || forTypes.includes(firmType))}
      logo={
        <div className="flex items-center gap-2">
          {/* This extra div is used to have the dynamic width of the logo */}
          <GarantiePlusLogo className="h-8 w-auto" />
        </div>
      }
    />
  );
}
